// playerService.js
// Fetches real player data from Firestore playerData/{sport} subcollection.
// Each document is one player with fields: name, city, jerseyNumber, year,
// overallRating, position — identical shape to the iOS bundled JSON files.
//
// Caches each sport's full player pool in memory for the session so we
// only read Firestore once per sport per page load, not once per draft pick.

import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

// ─── Sport name mapping ───────────────────────────────────────────────────────
// Firestore collection names match iOS Sport.rawValue (capitalized)

const SPORT_COLLECTION = {
  football:   "Football",
  baseball:   "Baseball",
  basketball: "Basketball",
  hockey:     "Hockey",
  soccer:     "Soccer",
};

// ─── In-memory cache ──────────────────────────────────────────────────────────
// Keyed by sport ID. Populated on first fetch, reused for the rest of the session.

const playerCache = {};

// ─── Position eligibility ─────────────────────────────────────────────────────
// Mirrors JSONPlayerDataService.getPlayersForPosition() exactly.
// Maps each draft slot to all eligible player JSON positions.

const POSITION_ELIGIBILITY = {
  football: {
    OL:  ["OT", "OG", "C"],
    DB:  ["CB", "S"],
    TE:  ["TE", "FB"],
    RB:  ["RB", "FB"],
  },
  baseball: {
    DH:  ["DH", "1B", "LF", "RF"],
  },
  basketball: {
    G:   ["PG", "SG"],
    F:   ["SF", "PF"],
    BN:  ["PG", "SG", "SF", "PF", "C"],
  },
  soccer: {
    DEF: ["CB", "LB", "RB"],
    MID: ["CAM", "CM", "CDM"],
    W:   ["LW", "RW"],
    GK:  ["GK"],
    ST:  ["ST"],
  },
};

function canFillSlot(sportId, slot, playerPosition) {
  const overrides = POSITION_ELIGIBILITY[sportId];
  const eligible = overrides?.[slot.toUpperCase()];
  if (eligible) {
    return eligible.includes(playerPosition.toUpperCase());
  }
  return slot.toUpperCase() === playerPosition.toUpperCase();
}

// ─── Fetch all players for a sport ───────────────────────────────────────────
// Returns the cached array if already loaded, otherwise reads Firestore.

export async function fetchPlayersForSport(sportId) {
  if (playerCache[sportId]) {
    return playerCache[sportId];
  }

  const sportName = SPORT_COLLECTION[sportId];
  if (!sportName) throw new Error(`Unknown sport: ${sportId}`);

  const snap = await getDoc(doc(db, "playerData", sportName));
  if (!snap.exists()) throw new Error(`No player data found for ${sportName}`);
  const players = (snap.data().players || []).map(p => ({
    name:          p.name,
    city:          p.city,
    jerseyNumber:  p.jerseyNumber,
    year:          p.year,
    overallRating: p.overallRating,
    position:      p.position,
  }));

  playerCache[sportId] = players;
  return players;
}

// ─── Generate player candidates for a draft slot ─────────────────────────────
// Mirrors PlayerSelectionView.generateRandomPlayers() on iOS.
// Returns up to 5 shuffled players eligible for the given slot, with two
// protections:
//   1. Players already drafted this session are excluded (via excludedNames),
//      so a player on your team never reappears at a later slot — e.g. drafting
//      Kobe Bryant at SG means he won't show up again for a G or BN slot.
//   2. The 5 are de-duplicated by name, so the same player never appears twice
//      in one set — e.g. Peyton Manning (Indianapolis) and Peyton Manning
//      (Denver), or any player listed across multiple years.

export function generateCandidates(allPlayers, sportId, slot, excludedNames = new Set()) {
  const eligible = allPlayers.filter(
    p => canFillSlot(sportId, slot, p.position) && !excludedNames.has(p.name)
  );

  // Fallback: if exclusions deplete the pool, allow repeats from the eligible set
  const source = eligible.length >= 5
    ? eligible
    : allPlayers.filter(p => canFillSlot(sportId, slot, p.position));

  // Shuffle first so the surviving entry for any duplicated name is random, then
  // drop duplicate names before taking 5. This is the iOS removingDuplicates(by:
  // \.name) step: it stops the same player appearing twice in one set when they
  // exist in the pool under multiple teams/years (e.g. two Peyton Mannings).
  const shuffled = [...source].sort(() => Math.random() - 0.5);
  const seenNames = new Set();
  const deduped = [];
  for (const p of shuffled) {
    if (seenNames.has(p.name)) continue;
    seenNames.add(p.name);
    deduped.push(p);
  }

  return deduped.slice(0, 5);
}

// ─── Display position ─────────────────────────────────────────────────────────
// Mirrors Sport.displayPosition() from iOS exactly.
// For transparent/flex slots, shows the player's real position.
// For basketball G/F flex slots, also shows the real position during drafting.

const FLEX_DISPLAY_SLOTS = ["OL", "DB", "RB", "TE", "G", "F", "BN", "DEF", "MID", "W", "DH"];

export function displayPosition(slot, player) {
  if (FLEX_DISPLAY_SLOTS.includes(slot.toUpperCase())) {
    return player.position.toUpperCase();
  }
  return slot.toUpperCase();
}
